import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { MapPin, Loader2, RefreshCw, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Trigger + progress for the venue backfill (see routes/admin.py).
 * Iterates every event with a free-text venue_name but missing lat/lng and
 * resolves it against the venue catalog for the event's commune, filling
 * coordinates + address in-place. Idempotent.
 */
export default function VenueBackfillCard() {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/venues/backfill/status`);
      setStatus(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (status?.running) {
      if (!pollRef.current) pollRef.current = setInterval(fetchStatus, 2000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.running]);

  const start = async () => {
    setStarting(true);
    try {
      await axios.post(`${API_URL}/api/admin/venues/backfill`);
      toast.success('Backfill de recintos iniciado');
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar el backfill');
    } finally {
      setStarting(false);
    }
  };

  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;
  const done = !!status && status.total > 0 && status.processed >= status.total && !status.running;

  return (
    <Card className="rounded-2xl border-0 shadow-sm mb-6" data-testid="venue-backfill-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-emerald-700" />
          Recintos: auto-match retroactivo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          Recorre los eventos que tienen recinto escrito pero sin coordenadas y
          busca ese nombre en el catálogo de la misma comuna. Si hay match,
          rellena latitud, longitud y dirección para que aparezcan en el mapa.
          Idempotente: los eventos ya resueltos no se vuelven a tocar.
        </p>
        {status?.running && (
          <div className="space-y-2" data-testid="venue-backfill-running">
            <div className="flex items-center gap-2 text-sm text-emerald-800 font-semibold">
              <Loader2 className="w-4 h-4 animate-spin" />
              Procesando… {status.processed}/{status.total} eventos ({pct}%)
            </div>
            <Progress value={pct} />
          </div>
        )}
        {done && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex items-start gap-2" data-testid="venue-backfill-done">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-700" />
            <div>
              <div className="font-semibold">Backfill completado</div>
              <div className="text-xs mt-0.5">
                {status.matched} eventos resueltos · {status.skipped} sin match en el catálogo (necesitan crear el recinto o ingresar coords a mano)
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            onClick={start}
            disabled={starting || status?.running}
            className="bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50"
            data-testid="start-venue-backfill-btn"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {status?.running ? 'Procesando…' : (done ? 'Volver a correr' : 'Iniciar backfill')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
